import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ValutProgressOverlay } from "../Components/Atoms/ValutProgressOverlay";
import {
  dismissActivePageAlert,
  setProgressFeedbackActiveBridge,
} from "./usePageAlert";

export type ProgressAlertStatus = "loading" | "success" | "error";

export interface ProgressAlertOptions {
  message: string;
  description?: string;
}

export interface ProgressAlertResultOptions extends ProgressAlertOptions {
  autoCloseMs?: number;
}

/** Shape returned by API handlers; drives success (green) vs error (red) overlay. */
export interface ProgressOperationResult {
  success: boolean;
  message?: string;
  description?: string;
}

export interface RunWithProgressOptions {
  /** Called before loading overlay — use to close form/quote modals. */
  beforeProgress?: () => void;
  /** Mock-only: force error state for testing the red icon. */
  mockFail?: boolean;
}

export class ProgressOperationError extends Error {
  readonly result: ProgressOperationResult;

  constructor(result: ProgressOperationResult) {
    super(result.message ?? "Falha na operação");
    this.name = "ProgressOperationError";
    this.result = result;
  }
}

const MOCK_API_DELAY_MS = 1800;

/** MUI Dialog exit transition is ~225ms; wait so modals finish closing after beforeProgress. */
const MODAL_DISMISS_DELAY_MS = 350;

const waitForModalDismiss = () =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, MODAL_DISMISS_DELAY_MS);
  });

export const isProgressOperationResult = (value: unknown): value is ProgressOperationResult =>
  typeof value === "object" &&
  value !== null &&
  "success" in value &&
  typeof (value as ProgressOperationResult).success === "boolean";

/** Simulates an API call until real endpoints are wired. */
export const mockProgressApi = (
  options: {
    delay?: number;
    fail?: boolean;
    message?: string;
    description?: string;
  } = {}
): Promise<ProgressOperationResult> =>
  new Promise((resolve) => {
    setTimeout(() => {
      if (options.fail) {
        resolve({
          success: false,
          message: options.message ?? "Falha na operação",
          description:
            options.description ?? "Não foi possível concluir a operação. Tente novamente.",
        });
        return;
      }

      resolve({ success: true });
    }, options.delay ?? MOCK_API_DELAY_MS);
  });

interface ProgressAlertState {
  open: boolean;
  status: ProgressAlertStatus;
  message: string;
  description?: string;
}

interface ProgressAlertContextData {
  showProgress(options: ProgressAlertOptions): void;
  showSuccess(options: ProgressAlertResultOptions): void;
  showError(options: ProgressAlertResultOptions): void;
  hideProgress(): void;
  runWithProgress<T>(
    task: () => Promise<T>,
    messages: {
      loading: string;
      loadingDescription?: string;
      success?: string;
      successDescription?: string;
      error?: string;
      errorDescription?: string;
    },
    options?: RunWithProgressOptions
  ): Promise<T>;
  isOpen: boolean;
  status: ProgressAlertStatus;
}

const DEFAULT_SUCCESS_CLOSE_MS = 2800;
const DEFAULT_ERROR_CLOSE_MS = 3200;

const waitForConclusion = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const INITIAL_STATE: ProgressAlertState = {
  open: false,
  status: "loading",
  message: "",
  description: undefined,
};

const setProgressBodyLock = (active: boolean) => {
  document.body.classList.toggle("valut-progress-active", active);
};

const ProgressAlertContext = createContext<ProgressAlertContextData>(
  {} as ProgressAlertContextData
);

const ProgressAlertProvider: React.FC = ({ children }) => {
  const [state, setState] = useState<ProgressAlertState>(INITIAL_STATE);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressActiveRef = useRef(false);

  const syncProgressActive = useCallback((active: boolean) => {
    progressActiveRef.current = active;
    setProgressFeedbackActiveBridge(() => progressActiveRef.current);
    setProgressBodyLock(active);
  }, []);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const hideProgress = useCallback(() => {
    clearCloseTimer();
    syncProgressActive(false);
    setState(INITIAL_STATE);
  }, [clearCloseTimer, syncProgressActive]);

  const scheduleClose = useCallback(
    (delay: number) => {
      clearCloseTimer();
      closeTimerRef.current = setTimeout(() => {
        syncProgressActive(false);
        setState(INITIAL_STATE);
        closeTimerRef.current = null;
      }, delay);
    },
    [clearCloseTimer, syncProgressActive]
  );

  const showProgress = useCallback(
    ({ message, description }: ProgressAlertOptions) => {
      clearCloseTimer();
      dismissActivePageAlert();
      syncProgressActive(true);
      setState({
        open: true,
        status: "loading",
        message,
        description,
      });
    },
    [clearCloseTimer, syncProgressActive]
  );

  const showSuccess = useCallback(
    ({ message, description, autoCloseMs = DEFAULT_SUCCESS_CLOSE_MS }: ProgressAlertResultOptions) => {
      clearCloseTimer();
      dismissActivePageAlert();
      syncProgressActive(true);
      setState({
        open: true,
        status: "success",
        message,
        description,
      });
      scheduleClose(autoCloseMs);
    },
    [clearCloseTimer, scheduleClose, syncProgressActive]
  );

  const showError = useCallback(
    ({ message, description, autoCloseMs = DEFAULT_ERROR_CLOSE_MS }: ProgressAlertResultOptions) => {
      clearCloseTimer();
      dismissActivePageAlert();
      syncProgressActive(true);
      setState({
        open: true,
        status: "error",
        message,
        description,
      });
      scheduleClose(autoCloseMs);
    },
    [clearCloseTimer, scheduleClose, syncProgressActive]
  );

  const runWithProgress = useCallback(
    async <T,>(
      task: () => Promise<T>,
      messages: {
        loading: string;
        loadingDescription?: string;
        success?: string;
        successDescription?: string;
        error?: string;
        errorDescription?: string;
      },
      options?: RunWithProgressOptions
    ): Promise<T> => {
      if (options?.beforeProgress) {
        options.beforeProgress();
        await waitForModalDismiss();
      }

      showProgress({
        message: messages.loading,
        description: messages.loadingDescription,
      });

      try {
        const result = await task();

        if (isProgressOperationResult(result)) {
          if (!result.success) {
            const errorCloseMs = DEFAULT_ERROR_CLOSE_MS;
            showError({
              message: result.message ?? messages.error ?? "Falha na operação",
              description: result.description ?? messages.errorDescription,
              autoCloseMs: errorCloseMs,
            });
            await waitForConclusion(errorCloseMs);
            throw new ProgressOperationError(result);
          }

          const successCloseMs = DEFAULT_SUCCESS_CLOSE_MS;
          showSuccess({
            message: result.message ?? messages.success ?? "Concluído",
            description: result.description ?? messages.successDescription,
            autoCloseMs: successCloseMs,
          });
          await waitForConclusion(successCloseMs);
          return result as T;
        }

        const successCloseMs = DEFAULT_SUCCESS_CLOSE_MS;
        showSuccess({
          message: messages.success ?? "Concluído",
          description: messages.successDescription,
          autoCloseMs: successCloseMs,
        });
        await waitForConclusion(successCloseMs);
        return result;
      } catch (error) {
        if (error instanceof ProgressOperationError) {
          throw error;
        }

        const fallback =
          error instanceof Error ? error.message : "Não foi possível concluir a operação.";
        const errorCloseMs = DEFAULT_ERROR_CLOSE_MS;
        showError({
          message: messages.error ?? "Falha na operação",
          description: messages.errorDescription ?? fallback,
          autoCloseMs: errorCloseMs,
        });
        await waitForConclusion(errorCloseMs);
        throw error;
      }
    },
    [showError, showProgress, showSuccess]
  );

  useEffect(() => {
    setProgressFeedbackActiveBridge(() => progressActiveRef.current);
    return () => {
      progressActiveRef.current = false;
      setProgressFeedbackActiveBridge(() => false);
    };
  }, []);

  useEffect(() => () => {
    clearCloseTimer();
    progressActiveRef.current = false;
    setProgressFeedbackActiveBridge(() => false);
    setProgressBodyLock(false);
  }, [clearCloseTimer]);

  const contextValue = useMemo(
    () => ({
      showProgress,
      showSuccess,
      showError,
      hideProgress,
      runWithProgress,
      isOpen: state.open,
      status: state.status,
    }),
    [hideProgress, runWithProgress, showError, showProgress, showSuccess, state.open, state.status]
  );

  return (
    <ProgressAlertContext.Provider value={contextValue}>
      {children}
      <ValutProgressOverlay
        open={state.open}
        status={state.status}
        message={state.message}
        description={state.description}
      />
    </ProgressAlertContext.Provider>
  );
};

function useProgressAlert() {
  const context = useContext(ProgressAlertContext);
  if (!context) {
    throw new Error("useProgressAlert must be used within a ProgressAlertProvider");
  }
  return context;
}

export { ProgressAlertProvider, useProgressAlert };
